import React from "react";
import { Select } from "../../components/CustomComponent.js";
import { getApiResponses, groupApiResponses } from "../../assets/files/api_responses.js";
import { generateSnippets } from "./snippets.js";
import { CodeBlock } from "./ui.js";

// วงเงินต่อรายการที่แสดงใต้ endpoint (ฝาก/ถอนมาจากการตั้งค่าบัญชีใน DB ตัวเลขนี้คือค่า default · settlement เป็นค่าตายตัวในโค้ด)
const AMOUNT_LIMITS = {
  "deposits Thai": { label: "Limit Deposit", range: "300.00 - 2,000,000.00 THB" },
  "withdrawals Thai": { label: "Limit Withdrawals", range: "300.00 - 2,000,000.00 THB" },
  "deposits Laos": { label: "Limit Deposit", range: "30,000.00 - 140,000,000.00 LAK", qr: "The QR code is valid for 20 minutes (until expire_datetime)." },
  "withdrawals Laos": { label: "Limit Withdrawals", range: "30,000.00 - 140,000,000.00 LAK" },
  "settlement HQ Thai": { label: "Minimum Settlement", range: "300.00 THB", note: "The amount must not exceed your settled (T+1) balance." },
  "settlement HQ Laos": { label: "Minimum Settlement", range: "30,000.00 LAK", note: "The amount must not exceed your settled (T+1) balance." },
};

// รายละเอียด endpoint หนึ่งตัว (item จาก Postman collection) ใน /document-api?i=api:<name>
export default function ApiReference({ selected, type, responseTab, onType, onResponseTab }) {
  const response_tab = responseTab;
  const responses = getApiResponses(selected);
  const activeResponse = responses[response_tab] || responses[0] || null;
  const responseText = activeResponse ? JSON.stringify(activeResponse.body, null, 2) : "";
  return (
    <>
              <div className="row">
                <div className="col-12 col-lg-6">
                  {/* Method */}
                  <h5 className="fw-bold mb-3">Method</h5>
                  <div className="w-100 rounded-3 px-3 py-2 bg-light mb-3 hpx-40">
                    <span>
                      {selected?.request?.method?.toUpperCase() || " "}
                    </span>
                  </div>
                  {/* URL */}
                  <h5 className="fw-bold mb-3">URL</h5>
                  <CodeBlock inline text={`${selected?.request?.url?.host}/${selected?.request?.url?.path.join("/")}`} />
                  {/* Authentication */}
                  {selected?.request?.auth && (
                    <div>
                      <h5 className="fw-bold mb-3">Authentication</h5>
                      <table className="table table-striped-columns table-hover mb-3">
                        <thead>
                          <tr>
                            <td className="wpx-150">Key</td>
                            <td>Value</td>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{selected?.request?.auth?.type}</td>
                            <td>{`{{token}}`}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Header */}
                  <div>
                    <h5 className="fw-bold mb-3">Header</h5>
                    <table className="table table-striped-columns table-hover mb-3">
                      <thead>
                        <tr>
                          <td className="wpx-150">Key</td>
                          <td>Value</td>
                        </tr>
                      </thead>
                      <tbody>
                        {selected?.request?.header.map((item, index) => (
                          <tr key={`array-${index}`}>
                            <td>{item.key}</td>
                            <td>{item.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Body */}
                  {selected?.request?.body?.raw && (
                    <div>
                      <h5 className="fw-bold mb-3">Body</h5>
                      <CodeBlock text={selected?.request?.body?.raw} />
                      {selected?.name === "verify account" && (
                        <p className="fw-bold text-danger mb-2">
                          This endpoint uses a separate public API token issued
                          by us, not the token from auth.
                        </p>
                      )}
                      {selected?.name === "update customer batch" && (
                        <p className="text-secondary mb-2">
                          Items without a matching uid, or whose bank account
                          duplicates another customer, are skipped.
                        </p>
                      )}
                      {selected?.name === "verify account" && (
                        <div>
                          <div>
                            <div className="fw-bold text-danger mb-2">Note: The `BODY` attribute only needs to contain one variable. For example, if you want to check with `bank_account`, simply send `bank_account`.</div>
                            <div className="w-100 rounded-3 px-3 py-2 bg-light mb-3 position-relative pe-5">
                              <pre>
                                {JSON.stringify({ "bank_account": ["1234567890"] },null,2)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      )}

                      {selected?.request?.body?.raw
                        .replace(/\[.*?\]/gs, "")
                        .replaceAll("{", "")
                        .replaceAll("}", "")
                        .trim()
                        .split("\n")
                        .map((item, index) => (
                          <div>
                            <div className="d-flex flex-wrap justify-content-between">
                              <div>
                                <b>
                                  {item
                                    .split(":")[0]
                                    .replaceAll('"', "")
                                    .replaceAll("//Require", "")
                                    .trim()}
                                </b>
                                <span className="ms-3 text-secondary">
                                  {global.api_description.find(
                                    (e) =>
                                      e.key ===
                                      item
                                        .split(":")[0]
                                        .replaceAll('"', "")
                                        .replaceAll("//Require", "")
                                        .trim()
                                  )?.type || "-"}
                                </span>
                              </div>
                              {item.includes("Require") && (
                                <span className="text-orange">required</span>
                              )}
                            </div>
                            <div className="text-secondary">
                              {global.api_description.find(
                                (e) =>
                                  e.key ===
                                  item
                                    .split(":")[0]
                                    .replaceAll('"', "")
                                    .replaceAll("//Require", "")
                                    .trim()
                              )?.description || "-"}
                            </div>
                            <div className="text-secondary">
                              length :{" "}
                              {(() => {
                                const key = item
                                  .split(":")[0]
                                  .replace(/["]|\/\/Require/g, "")
                                  .trim();
                                const data = global.api_description.find(
                                  (e) => e.key === key
                                );
                                return data?.min && data?.max
                                  ? `${data.min} - ${data.max}`
                                  : "-";
                              })()}
                            </div>
                            <hr />
                          </div>
                        ))}
                    </div>
                  )}
                  
                  {/* Parameters */}
                  {selected?.request?.url?.query &&
                    selected?.request?.url?.query.length > 0 && (
                      <div>
                        <h5 className="fw-bold mb-3">Parameters</h5>
                        {selected?.request?.url?.query.map((item, index) => (
                          <div>
                            <div className="d-flex flex-wrap justify-content-between">
                              <div>
                                <b>{item.key}</b>
                                <span className="ms-3 text-secondary">
                                  {item.type}
                                </span>
                              </div>
                            </div>
                            <div className="text-secondary">
                              {item.description}
                            </div>
                            <div className="text-secondary">
                              length :{" "}
                              {item.min && item.max
                                ? `${item.min} - ${item.max}`
                                : "-"}
                            </div>
                            <hr />
                          </div>
                        ))}
                      </div>
                    )}
                </div>
                {/* Command */}
                <div className="col-12 col-lg-6">
                  <h5 className="fw-bold mb-3">Command</h5>
                  <Select
                    className="form-control mb-3"
                    options={[
                      "cURL",
                      "Node",
                      "Python",
                      "GO",
                      "C#",
                      "JAVA",
                      "PHP",
                    ].map((e) => {
                      return {
                        value: e.replaceAll("#", "sharp").toLowerCase(),
                        label: e,
                      };
                    })}
                    onChange={(e) => {
                      onType(e);
                    }}
                    value={type}
                  />
                  {type === "java" && (
                    <div>
                      <h5 className="fw-bold mb-3">Tips</h5>
                      <h6>
                        How to Convert Certificate Files to .p12 and Use in Java
                        Applications
                      </h6>
                      <p>
                        1. Convert your certificate files into ".p12" format
                        using this command:
                      </p>
                      <CodeBlock
                      className="mb-3"
                      text={`openssl pkcs12 -export \\
  -in certificate.pem \\
  -inkey private.key \\
  -out certificate.p12 \\
  -name "client-cert"`}
                    />
                      <span className="fw-bold mb-3">Explanation:</span>
                      <ul className="mb-3">
                        <li>
                          <b>"certificate.pem"</b> is your digital certificate
                          (like your digital ID card).
                        </li>
                        <li>
                          <b>"private.key"</b> is your private key (used to
                          prove your identity).
                        </li>
                        <li>
                          This command combines them into one file:
                          certificate.p12, which is easier to use in Java.
                        </li>
                      </ul>
                      <p>2. You’ll be asked to enter a password</p>
                      <p className="mb-3">
                        When you run the command, you’ll see:
                      </p>
                      <div className="w-100 rounded-3 px-3 py-2 bg-light mb-3 position-relative pe-5">
                        <pre>{`Enter Export Password: 
Verifying - Enter Export Password:`}</pre>
                      </div>
                      <span className="fw-bold mb-3">Important:</span>
                      <p className="mb-3">
                        The password you type here must be remembered. You’ll
                        use it in your Java code like this:
                      </p>
                      <div className="w-100 rounded-3 px-3 py-2 bg-light mb-3 position-relative pe-5">
                        <pre>{`char[] password = "your_password".toCharArray();`}</pre>
                      </div>
                      <p>3. No truststore file is required</p>
                      <p>
                        Our API is served by a publicly trusted certificate, so
                        your runtime already trusts it through its own root
                        store. Pass <b>null</b> for the TrustManagers and let
                        the JDK use its built-in <b>cacerts</b>:
                      </p>
                      <div className="w-100 rounded-3 px-3 py-2 bg-light mb-3 position-relative pe-5">
                        <pre>{`sc.init(kmf.getKeyManagers(), null, null);`}</pre>
                      </div>
                      <span className="fw-bold mb-3">Important:</span>
                      <p className="mb-3">
                        Do not load a custom truststore that holds only our
                        server certificate. Doing so replaces the root store and
                        pins you to a single certificate, so your integration
                        will stop working the next time that certificate is
                        rotated. Earlier integrations were given a{" "}
                        <b>cert.jks</b> file for this purpose; it is no longer
                        needed and should be removed.
                      </p>
                      <hr />
                      <span className="fw-bold mb-3">Summary:</span>
                      <ul className="mb-3">
                        <li>
                          <b>"certificate.pem"</b> = Identifies the client/user
                          (you)
                        </li>
                        <li>
                          <b>"private.key"</b> = Proves that identity is yours
                        </li>
                        <li>
                          The server side needs no file from you: your system's
                          CA bundle verifies our certificate
                        </li>
                      </ul>
                    </div>
                  )}
                  {selected?.name === "auth" && (
                    <div className="mb-3">
                      <h5 className="fw-bold mb-3">Note</h5>
                      <p>
                        When you request authentication, you will receive a{" "}
                        <b>token</b> to use for API calls. This token is valid
                        for <b>30 minutes</b>.
                      </p>
                      <p>
                        You can keep and reuse the issued token within its
                        lifetime. It is also possible to request a new token
                        before the current one expires, but{" "}
                        <b className="text-danger">
                          do not request too frequently
                        </b>
                        .
                      </p>
                      <p>
                        You{" "}
                        <b className="text-danger">
                          do not need to request authentication for every API
                          call
                        </b>
                        . A token is valid for <b>30 minutes</b>. Token requests
                        are rate limited to <b>100 requests per 5 minutes per
                        IP</b>. If exceeded you receive HTTP 429 (<b>P-4029</b>)
                        with the time the next request is allowed; the limit
                        resets automatically.
                      </p>
                    </div>
                  )}
                  {selected?.name === "get transactions" && (
                    <div className="mb-3">
                      <h5 className="fw-bold mb-3">Status</h5>
                      <ul>
                        <li>
                          <div className="d-flex">
                            <div className="wpx-100 fw-bold">pending</div>{" "}
                            <span>Awaiting payment from customer</span>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex">
                            <div className="wpx-100 fw-bold">success</div>{" "}
                            <span>Payment completed</span>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex">
                            <div className="wpx-100 fw-bold">cancel</div>{" "}
                            <span>
                              Payment canceled or payment transaction expired
                            </span>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex">
                            <div className="wpx-100 fw-bold">mismatch</div>{" "}
                            <span>
                              Bank information does not match registered
                              information
                            </span>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex">
                            <div className="wpx-100 fw-bold">refund</div>{" "}
                            <span>
                              Payment has been refunded to the customer
                            </span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  )}
                  {AMOUNT_LIMITS[selected?.name] && (
                    <div className="mb-3">
                      <h5 className="fw-bold mb-3">{AMOUNT_LIMITS[selected.name].label}</h5>
                      <h5>{AMOUNT_LIMITS[selected.name].range}</h5>
                      <p className="text-secondary">
                        {AMOUNT_LIMITS[selected.name].note ||
                          "Minimum and maximum amounts depend on your account configuration. The range above is the default; please contact us to confirm your limits."}
                      </p>
                      {AMOUNT_LIMITS[selected.name].qr && <p className="fw-bold">{AMOUNT_LIMITS[selected.name].qr}</p>}
                      {selected.name.includes("Laos") && !selected.name.includes("settlement") && (
                        <>
                          <h5 className="text-danger fw-bold mb-3">Caution</h5>
                          <h5 className="text-danger">
                            The amount submitted in Lao transactions must be an
                            integer or decimal value of .00
                          </h5>
                        </>
                      )}
                    </div>
                  )}
                  {selected?.name?.includes("refund mismatch") && (
                    <div className="mb-3">
                      <h5 className="fw-bold mb-3">Note</h5>
                      <p>
                        The <b>ref_no</b> provided must belong to a transaction
                        with the status
                        <b> "mismatch"</b> in order to request a refund.
                      </p>
                      <p>
                        The <b>amount</b> must be greater than 0 and less than or
                        equal to the deposited amount. Each <b>ref_no</b> can be
                        refunded only once.
                      </p>
                    </div>
                  )}
                  <h5 className="fw-bold mb-3">Sample Code</h5>
                  <CodeBlock text={generateSnippets(selected?.request, type)} />
                  <h5 className="fw-bold mb-3">Response</h5>
                  {/* tab สลับตัวอย่าง response: success + error ที่ endpoint นี้ตอบได้ */}
                  <div className="doc-tab-groups">
                    {groupApiResponses(responses).map((g) => (
                      <div className="doc-tab-group" key={`response-group-${g.group}`}>
                        <div className="doc-tab-group-label">{g.group}</div>
                        <div className="doc-tabs">
                          {g.items.map((r) => (
                            <span
                              key={`response-tab-${r.index}`}
                              className={`doc-tab ${r.index === (responses[response_tab] ? response_tab : 0) ? "active" : ""}`}
                              onClick={() => onResponseTab(r.index)}
                            >
                              <span className={`doc-tab-code ${r.http < 300 ? "ok" : r.http < 500 ? "warn" : "err"}`}>{r.http}</span>
                              {r.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {activeResponse?.when ? <div className="doc-response-when">{activeResponse.when}</div> : null}
                  <CodeBlock text={responseText} />
                  {selected?.name === "get bank" && (
                    <div>
                      <h5 className="fw-bold mb-3">Remark</h5>
                      <p>
                        The country variable refers to the bank operating in
                        each country.
                        <div className="w-100 rounded-3 px-3 py-2 my-3 bg-light position-relative">
                          <pre>
                            {`{
  "uid": "00000000-0000-0000-0000-000000000000",
  "code": "KBANK",
  "name_en": "Kasikorn Bank",
  "name_th": "ธนาคารกสิกรไทย",
  "country": [
    "THAI",
    "LAOS"
  ]
}`}
                          </pre>
                        </div>
                        For example, the bank name KBANK represents KBANK and
                        can be used to create customers in both Thailand and
                        Laos.
                      </p>
                    </div>
                  )}
                </div>
              </div>
    </>
  );
}
